import type { ReferralChannel, ReferralUtm, ShopInvite, ShopInviteBindResult } from "@osmix/contracts/seller-clients";
import type { CartPromoFeed } from "@osmix/contracts/promo-partners";
import type {
	CreateShopCustomOrder,
	ShopCustomOrder,
	ShopCustomOrderList,
} from "@osmix/contracts/shop-custom-order";

// fd-boutique-13: индивидуальный заказ с витрины — общие с шлюзом схемы.
export type {
	CreateShopCustomOrder,
	ShopCustomOrder,
	ShopCustomOrderList,
	ShopCustomOrderPaymentType,
	ShopCustomOrderSettings,
	ShopCustomOrderStatus,
} from "@osmix/contracts/shop-custom-order";

/** Откуда переход: прямая ссылка или QR из генератора кабинета (utm_source=qr_generator), и метки ссылки. */
export type ReferralVisitMeta = { channel?: ReferralChannel; utm?: ReferralUtm };
// Wire shapes + the buyer api surface, as zod schemas with inferred TS types.
// The surface (`Api`) is grouped by entity — `api.products.list()`. Both
// providers implement it 1:1: `./http.ts` (the real @osmix/api-gateway, the
// source of truth — default in the running stack) and `./mock/` (offline dev).
// Every response is parsed against these schemas at the boundary.
import type {
	NotificationItem,
	NotificationListQuery,
	NotificationLocale,
	NotificationPage,
	NotificationReadBySourceRequest,
	NotificationUnreadCount,
	TelegramLinkStart,
	TelegramLinkStatus,
} from "@osmix/contracts/notification";
export {
	NotificationItemSchema,
	NotificationPageSchema,
	NotificationUnreadCountSchema,
} from "@osmix/contracts/notification";
export type { TelegramLinkStart, TelegramLinkStatus } from "@osmix/contracts/notification";
export { TelegramLinkStartSchema, TelegramLinkStatusSchema } from "@osmix/contracts/notification";
export type {
	NotificationAction,
	NotificationItem,
	NotificationListQuery,
	NotificationLocale,
	NotificationPage,
	NotificationReadBySourceRequest,
	NotificationUnreadCount,
} from "@osmix/contracts/notification";
// Публичные соц-данные карточки товара — отзывы и вопросы (fd-review-02/03).
// Схемы живут в контрактах, здесь их не дублируем: реэкспортируем значения для
// парсинга на границе (http.ts) и типы для UI.
export {
	ProductReviewSchema,
	ProductReviewSummarySchema,
	ProductReviewListSchema,
	ProductReviewSortSchema,
	ProductQuestionSchema,
	ProductQuestionListSchema,
} from "@osmix/contracts/product-social";
export type {
	ProductReview,
	ProductReviewSummary,
	ProductReviewList,
	ProductReviewSort,
	ProductQuestion,
	ProductQuestionList,
} from "@osmix/contracts/product-social";
import type {
	ProductQuestion,
	ProductQuestionList,
	ProductReviewList,
	ProductReviewSort,
} from "@osmix/contracts/product-social";
// Маркетплейс запросов покупателя на поиск и изготовление (Э6, fd-request-*).
// Схемы живут в контрактах: реэкспортируем значения для парсинга на границе
// (http.ts) и типы для UI.
export {
	BuyerRequestTypeSchema,
	BuyerRequestStatusSchema,
	BuyerRequestAttachmentSchema,
	BuyerRequestSchema,
	BuyerRequestDetailSchema,
	BuyerRequestListSchema,
	RequestOfferSchema,
	RequestOfferStatusSchema,
	CreateBuyerRequestSchema,
	UpdateBuyerRequestSchema,
} from "@osmix/contracts/buyer-request";
export type {
	BuyerRequest,
	BuyerRequestType,
	BuyerRequestStatus,
	BuyerRequestAttachment,
	BuyerRequestDetail,
	BuyerRequestList,
	RequestOffer,
	RequestOfferStatus,
	CreateBuyerRequest,
	UpdateBuyerRequest,
} from "@osmix/contracts/buyer-request";
import type {
	BuyerRequestAttachment,
	BuyerRequestDetail,
	BuyerRequestList,
	CreateBuyerRequest,
	RequestOffer,
	UpdateBuyerRequest,
} from "@osmix/contracts/buyer-request";
// Центр помощи, настройки уведомлений и Яндекс.Метрика (Э5/Э6). Схемы живут в
// контрактах. ⚠️ Subpath пакета не резолвится dev-бандлером client-web, поэтому
// импортируем строго из корневого `@osmix/contracts`.
export {
	FaqArticleSchema,
	FaqCategoryGroupSchema,
	FaqListSchema,
	SupportTicketStatusSchema,
	SupportTicketMessageSchema,
	SupportTicketSchema,
	SupportTicketDetailSchema,
	SupportTicketListSchema,
	CreateTicketRequestSchema,
	TicketMessageRequestSchema,
	NotificationPreferencesSchema,
	NotificationCategoryTogglesSchema,
	NotificationMarketingFrequencySchema,
	NotificationCategorySchema,
	DEFAULT_NOTIFICATION_PREFERENCES,
	MetrikaConfigSchema,
	SeoPageSchema,
	WebPushConfigSchema,
} from "@osmix/contracts";
export type {
	SeoPage,
	WebPushConfig,
	WebPushSubscriptionInput,
	FaqArticle,
	FaqCategoryGroup,
	FaqList,
	SupportTicket,
	SupportTicketDetail,
	SupportTicketMessage,
	SupportTicketStatus,
	CreateTicketRequest,
	TicketMessageRequest,
	NotificationPreferences,
	NotificationCategory,
	NotificationCategoryToggles,
	NotificationMarketingFrequency,
	NotificationPreferencesUpdate,
	MetrikaConfig,
	NotificationTopic,
	NotificationTopicCounts,
	NotificationTopicSettings,
	NotificationTopicSettingsUpdate,
} from "@osmix/contracts";
import type {
	SeoPage,
	WebPushConfig,
	WebPushSubscriptionInput,
	FaqList,
	SupportTicket,
	SupportTicketDetail,
	CreateTicketRequest,
	NotificationPreferences,
	NotificationPreferencesUpdate,
	MetrikaConfig,
	NotificationTopic,
	NotificationTopicCounts,
	NotificationTopicSettings,
	NotificationTopicSettingsUpdate,
} from "@osmix/contracts";
export {
	NotificationTopicSchema,
	NotificationTopicSettingsSchema,
	NotificationTopicCountsSchema,
	notificationTopicOfKind,
} from "@osmix/contracts";

import { z } from "zod";

import type { CabinetApi } from "./cabinet";

// Параметры ленты публичных отзывов товара (fd-review-02).
export type ProductReviewsQuery = {
	sort?: ProductReviewSort;
	rating?: number;
	withPhotos?: boolean;
	variant?: string;
};

// ── Products ───────────────────────────────────────────────────────────────
export const ProductStatus = z.enum([
	"draft",
	"moderation",
	"rejected",
	"published",
	"blocked",
	"archived",
]);
export type ProductStatus = z.infer<typeof ProductStatus>;

const ProductBadge = z.enum(["handmade", "eco", "new", "limited", "customizable", "bestseller", "sale"]);
export type ProductBadge = z.infer<typeof ProductBadge>;
/** Оси вариантов сверх цвета и размера: материал, свой параметр (шлюз, миграция 531). */
const ProductVariantAxis = z.object({ key: z.string(), label: z.string() });
/** Точки на фото галереи с мини-карточкой товара (шлюз, миграция 531). */
const ProductPhotoHotspots = z.object({
	photo: z.string(),
	pins: z.array(
		z.object({
			id: z.string(),
			x: z.number(),
			y: z.number(),
			productId: z.string(),
			product: z
				.object({
					id: z.string(),
					name: z.string(),
					brand: z.string(),
					price: z.number(),
					oldPrice: z.number().optional(),
					photo: z.string().optional(),
				})
				.optional(),
		}),
	),
});
export type ProductPhotoHotspots = z.infer<typeof ProductPhotoHotspots>;
const RichDescription = z.object({
	format: z.literal("osmix-rich-text"),
	blocks: z.array(z.object({ type: z.enum(["paragraph", "heading", "bullet"]), text: z.string() })),
});
const ProductAttributeValue = z.union([z.string(), z.number(), z.boolean()]);
const ProductCustomAttribute = z.object({ label: z.string(), value: z.string() });
/** Свои мерки продавца к таблице размеров (длина рукава, спинки…; шлюз, миграция 411). */
const ProductSizeMeasures = z.object({
	columns: z.array(z.string()),
	rows: z.record(z.string(), z.array(z.string())),
});
const ProductCustomization = z.object({
	enabled: z.boolean(),
	options: z.array(z.object({ name: z.string(), minPrice: z.number(), photo: z.string().optional() })),
});
const ProductReturnPolicy = z.object({
	standard14Days: z.boolean(),
	customOrderException: z.boolean(),
	notes: z.string().optional(),
});

const ProductSellerInfo = z.object({
	shopId: z.string(),
	name: z.string(),
	slug: z.string().optional(),
	rating: z.number(),
	ratingCount: z.number(),
});

const ProductModerationEvent = z.object({
	id: z.string(),
	productId: z.string(),
	moderatorId: z.string().optional(),
	actorId: z.string().optional(),
	oldStatus: ProductStatus,
	newStatus: ProductStatus,
	reason: z.string().optional(),
	createdAt: z.string(),
});

const ProductVariant = z.object({
	id: z.string(),
	color: z.string(),
	size: z.string(),
	article: z.string().optional(),
	options: z.record(z.string(), z.string()).optional(),
	price: z.number(),
	oldPrice: z.number().optional(),
	stock: z.number(),
	photos: z.array(z.string()),
	position: z.number().optional(),
	// Подписи размера и мерки «Таблицы размеров» (обхваты, см) — считает шлюз
	// по сетке категории товара; у детской ростовки и вне одежды их нет.
	sizeLabel: z.string().optional(),
	sizeSecondaryLabel: z.string().optional(),
	sizeChest: z.string().optional(),
	sizeWaist: z.string().optional(),
	sizeHips: z.string().optional(),
	weightGrams: z.number().optional(),
	lengthCm: z.number().optional(),
	widthCm: z.number().optional(),
	heightCm: z.number().optional(),
});

/** Товар группы вариантов: каждый вариант — отдельный товар (шлюз, миграция 295). */
const ProductGroupVariant = z.object({
	id: z.string(),
	current: z.boolean(),
	name: z.string(),
	article: z.string(),
	color: z.string(),
	colorHex: z.string().optional(),
	photo: z.string().optional(),
	status: ProductStatus,
	price: z.number(),
	oldPrice: z.number().optional(),
	stock: z.number(),
	available: z.boolean(),
});

export const Product = z.object({
	id: z.string(),
	shopId: z.string(),
	name: z.string(),
	article: z.string(),
	brand: z.string(),
	categoryId: z.string(),
	category: z.string(),
	color: z.string(),
	sizes: z.array(z.string()),
	status: ProductStatus,
	/** Группа товаров-вариантов; другие цвета — `groupVariants` (только в карточке товара). */
	variantGroupId: z.string().optional(),
	groupVariants: z.array(ProductGroupVariant).optional(),
	sellerInfo: ProductSellerInfo.optional(),
	latestRejectionReason: z.string().optional(),
	moderationHistory: z.array(ProductModerationEvent).optional(),
	price: z.number(),
	oldPrice: z.number().optional(),
	/** fd-promo-03: действующая акция магазина — `price` уже со скидкой акции. */
	activePromo: z
		.object({
			id: z.string(),
			title: z.string(),
			discountType: z.enum(["percent", "amount"]),
			value: z.number(),
			endsAt: z.string(),
			price: z.number(),
		})
		.optional(),
	stock: z.number(),
	photos: z.array(z.string()),
	variants: z.array(ProductVariant).optional(),
	description: z.string(),
	descriptionRich: RichDescription.optional(),
	videoUrl: z.string().optional(),
	categoryAttributes: z.record(z.string(), ProductAttributeValue).optional(),
	customAttributes: z.array(ProductCustomAttribute).optional(),
	sizeMeasures: ProductSizeMeasures.optional(),
	/** Минимальный заказ, шт. (шлюз, миграция 422): бейдж «от N шт.», в корзину - сразу N. */
	minOrderQty: z.number().optional(),
	/** Оси вариантов: материал, свой параметр; значение у строки - `variant.options[key]`. */
	variantAxes: z.array(ProductVariantAxis).optional(),
	/** Точки на фото галереи (fd-catalog-03). */
	photoHotspots: z.array(ProductPhotoHotspots).optional(),
	customization: ProductCustomization.optional(),
	/**
	 * «Изготавливается под заказ» — выводится гейтвеем как «есть исключение по
	 * возврату ИЛИ включена кастомизация». Витрина этим флагом одна не
	 * пользуется: кастомизация перевешивает (борд §2.2 против §2.3).
	 */
	madeToOrder: z.boolean().optional(),
	/** Срок изготовления под заказ, дней (из карточки продавца). */
	madeToOrderLeadTimeDays: z.number().optional(),
	/**
	 * Действующий документ о соответствии (17.10.2026): номер, срок и ссылка на
	 * реестр Росаккредитации. Файл покупателю не отдаётся.
	 */
	complianceDocument: z
		.object({
			kind: z.enum(["certificate", "declaration", "refusal_letter", "state_registration"]),
			number: z.string(),
			validUntil: z.string().nullable(),
			checkUrl: z.string().nullable(),
		})
		.optional(),
	/** Гарантия, дней; `noWarranty` — товар гарантии не подлежит. */
	warrantyDays: z.number().optional(),
	noWarranty: z.boolean().optional(),
	returnPolicy: ProductReturnPolicy.optional(),
	tags: z.array(z.string()).optional(),
	badges: z.array(ProductBadge).optional(),
	keywords: z.array(z.string()),
	weightGrams: z.number().optional(),
	lengthCm: z.number().optional(),
	widthCm: z.number().optional(),
	heightCm: z.number().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type Product = z.infer<typeof Product>;

export const Meta = z.object({
	page: z.number(),
	pageSize: z.number(),
	total: z.number(),
	totalPages: z.number(),
});
export type Meta = z.infer<typeof Meta>;

export const ProductPage = z.object({
	data: z.array(Product),
	meta: Meta,
	/** Запрос из 8 цифр совпал с активным UGC-артикулом (ТЗ 31.10.2026): резолв
	 *  в форме `UgcResolveResult`, разбирается схемой контракта в lib/ugc-search. */
	ugcRedirect: z.unknown().optional(),
});
export type ProductPage = z.infer<typeof ProductPage>;

/**
 * Страница персональной ленты главной (`GET /feed/for-you`, п.16 правок 27.10).
 * Форма — как у каталога плюс курсор и seed прохода: с одним seed страницы не
 * повторяют товары, повтор страницы отдаёт её же.
 */
export const PersonalFeedPage = z.object({
	data: z.array(Product),
	meta: Meta.extend({
		nextCursor: z.string().nullable(),
		seed: z.string(),
		personalized: z.boolean(),
	}),
});
export type PersonalFeedPage = z.infer<typeof PersonalFeedPage>;

/** Запрос персональной ленты: фильтры каталога (жёсткие) + проход и курсор. */
export type PersonalFeedQuery = Omit<ProductQuery, "sort" | "page"> & {
	seed: string;
	cursor?: string | null;
	/** Анонимный id гостя (тот же, что у гостевых лайков и событий витрины). */
	clientId?: string;
};

export const ProductSort = z.enum(["relevance", "price-asc", "price-desc", "newest", "popular"]);
export type ProductSort = z.infer<typeof ProductSort>;

export type ProductQuery = {
	search?: string;
	shopId?: string;
	categoryId?: string;
	/** Несколько корней сразу (гейтвей принимает повторяющийся categoryId): напр.
	 *  «моя лента» в «Одежде» — по всем корням-аудиториям дерева. */
	categoryIds?: string[];
	brands?: string[];
	colors?: string[];
	sizes?: string[];
	priceMin?: number;
	priceMax?: number;
	inStock?: boolean;
	/** Раздел «Товар» панели фильтров: отмеченные типы, по ИЛИ (гейтвей —
	 *  `productType`). Пусто или все три — без ограничения. */
	productTypes?: ProductTypeKey[];
	/** Переключатель «SALE»: только товары со скидкой. */
	sale?: boolean;
	/** Регион покупателя — ассортимент под город (гейтвей учитывает доставку). */
	city?: string;
	/** fd-boutique-05: коллекция бутика (вместе с shopId). */
	collectionId?: string;
	sort?: ProductSort;
	page?: number;
	pageSize?: number;
};

/** Чипы «Товар»: в наличии / кастомизация / на заказ (значения гейтвея). */
export type ProductTypeKey = "in-stock" | "customizable" | "made-to-order";

// `value` is the filter key (category id, or brand/color/size string); `label`
// is the display name, present only when value isn't itself human-readable
// (categories: value = id, label = tree name).
export const FacetValue = z.object({
	value: z.string(),
	label: z.string().optional(),
	count: z.number(),
});
export type FacetValue = z.infer<typeof FacetValue>;

// Category facets mirror the managed tree: `value` is the category id, `label`
// the name, `count` rolls up the node plus its descendants. Selecting a node
// filters by it and all its children.
export type CategoryFacet = {
	value: string;
	label: string;
	count: number;
	children?: CategoryFacet[];
};
export const CategoryFacet: z.ZodType<CategoryFacet> = z.lazy(() =>
	z.object({
		value: z.string(),
		label: z.string(),
		count: z.number(),
		children: z.array(CategoryFacet).optional(),
	}),
);

/** Размеры одной шкалы: буквенные, одежда, обувь, детские, универсальные. */
export const SizeFacetGroup = z.object({
	scale: z.string(),
	label: z.string(),
	values: z.array(FacetValue),
});
export type SizeFacetGroup = z.infer<typeof SizeFacetGroup>;

export const ProductFacets = z.object({
	categories: z.array(CategoryFacet),
	brands: z.array(FacetValue),
	colors: z.array(FacetValue),
	sizes: z.array(FacetValue),
	/** Те же размеры по шкалам: в обуви не бывает S / M / L и наоборот. */
	sizeGroups: z.array(SizeFacetGroup).default([]),
	price: z.object({ min: z.number(), max: z.number() }),
});
export type ProductFacets = z.infer<typeof ProductFacets>;

// ── Categories (hierarchical) ────────────────────────────────────────────────
// Дерево v2 (миграция 134): до трёх уровней, раздел/пол/сетка — с наследованием,
// картинка категории — imageUrl (нет — клиент показывает заглушку).
export type Category = {
	id: string;
	name: string;
	children?: Category[];
	section?: string;
	gender?: string;
	sizeGrid?: string;
	imageUrl?: string;
};
export const Category: z.ZodType<Category> = z.lazy(() =>
	z.object({
		id: z.string(),
		name: z.string(),
		children: z.array(Category).optional(),
		section: z.string().optional(),
		gender: z.string().optional(),
		sizeGrid: z.string().optional(),
		imageUrl: z.string().optional(),
	}),
);

// Архивный узел дерева (GET /catalog/categories/archived): в дереве его нет, но
// на него ведут старые ссылки и сохранённые ленты. Преемник — по аудитории
// (`successors`) или по умолчанию (`successorId`); null — преемника нет.
export const ArchivedCategory = z.object({
	id: z.string(),
	name: z.string(),
	successorId: z.string().nullable(),
	successors: z
		.object({ female: z.string().optional(), male: z.string().optional(), kids: z.string().optional() })
		.nullable(),
});
export type ArchivedCategory = z.infer<typeof ArchivedCategory>;

// ── Shops + storefront ────────────────────────────────────────────────────────
export const Shop = z.object({
	id: z.string(),
	slug: z.string(),
	name: z.string(),
	tagline: z.string(),
	description: z.string(),
	logoUrl: z.string().optional(),
	heroUrl: z.string(),
	rating: z.number(),
	ratingCount: z.number(),
	productCount: z.number(),
	location: z.string(),
	since: z.string(),
	// «Проверенный продавец» (П7, fd-onboard-12): ИНН и все обязательные документы.
	verified: z.boolean().optional(),
});
export type Shop = z.infer<typeof Shop>;

const BannerSlide = z.object({
	id: z.string(),
	imageUrl: z.string().optional(),
	title: z.string().optional(),
	description: z.string().optional(),
	ctaUrl: z.string().optional(),
});

const AboutBlock = z.object({
	id: z.string(),
	kind: z.string(),
	enabled: z.boolean(),
	title: z.string().optional(),
	description: z.string().optional(),
	cards: z.array(
		z.object({
			id: z.string(),
			imageUrl: z.string().optional(),
			subtitle: z.string().optional(),
			description: z.string().optional(),
			// fd-boutique-08: год события таймлайна или номер шага процесса.
			label: z.string().optional(),
		}),
	),
	images: z.array(z.string()),
});

const InteractiveBlock = z.discriminatedUnion("type", [
	z.object({
		id: z.string(),
		type: z.literal("interior-pins"),
		slides: z.array(
			z.object({
				id: z.string(),
				imageUrl: z.string(),
				pins: z.array(
					z.object({ id: z.string(), x: z.number(), y: z.number(), productId: z.string() }),
				),
			}),
		),
	}),
	z.object({
		id: z.string(),
		type: z.literal("outfit-gallery"),
		outfits: z.array(
			z.object({ id: z.string(), imageUrl: z.string(), productIds: z.array(z.string()) }),
		),
	}),
	z.object({ id: z.string(), type: z.literal("banner"), slides: z.array(BannerSlide) }),
	z.object({
		id: z.string(),
		type: z.literal("about-brand"),
		heading: z.string(),
		body: z.string(),
		photoUrl: z.string().optional(),
	}),
]);

export const BoutiqueConfig = z.object({
	header: z.object({
		logoUrl: z.string().optional(),
		description: z.string(),
		customOrdersEnabled: z.boolean(),
	}),
	about: z.object({
		enabled: z.boolean(),
		titlePageEnabled: z.boolean(),
		blocks: z.array(AboutBlock),
	}),
	banner: z.object({ enabled: z.boolean(), slides: z.array(BannerSlide) }),
	pinnedProducts: z.array(z.string()),
	collections: z.object({
		groupByCollection: z.boolean(),
		items: z.array(
			z.object({
				id: z.string(),
				title: z.string(),
				productIds: z.array(z.string()),
				// fd-boutique-11: категории магазина для товарного блока.
				categoryIds: z.array(z.string()).optional(),
			}),
		),
	}),
	partnerProducts: z.array(z.string()),
	interactiveBlocks: z.array(InteractiveBlock),
	// fd-boutique-10: порядок блоков лендинга на витрине.
	blockOrder: z.array(z.string()).optional(),
	// fd-boutique-09: новости магазина и сезонная акция с подборкой SALE.
	news: z
		.array(
			z.object({
				id: z.string(),
				title: z.string(),
				body: z.string(),
				imageUrl: z.string().optional(),
				date: z.string(),
			}),
		)
		.optional(),
	seasonalSale: z
		.object({
			enabled: z.boolean(),
			title: z.string(),
			description: z.string().optional(),
			imageUrl: z.string().optional(),
		})
		.optional(),
	// fd-boutique-13: прайс и способы оплаты индивидуального заказа. Схема
	// своя (zod 4), форма — как ShopCustomOrderSettingsSchema контрактов.
	customOrder: z
		.object({
			productTypes: z.array(z.object({ id: z.string(), name: z.string(), price: z.number().optional() })),
			paymentTypes: z.array(z.enum(["card", "invoice"])),
			urgentMarkupPercent: z.number().optional(),
			leadDays: z.number().optional(),
			note: z.string().optional(),
		})
		.optional(),
	published: z.boolean(),
	updatedAt: z.string(),
});
export type BoutiqueConfig = z.infer<typeof BoutiqueConfig>;

// fd-boutique-12: резолвнутый партнёрский товар для витрины — карточка товара
// плюс атрибуция магазина-источника.
export const PartnerProduct = z.object({
	product: Product,
	partnerShopId: z.string(),
	partnerShopName: z.string().optional(),
});
export type PartnerProduct = z.infer<typeof PartnerProduct>;

export const Storefront = z.object({
	shop: Shop,
	config: BoutiqueConfig,
	// fd-boutique-12: резолвнутые товары партнёрского блока. Опционально —
	// старые витрины и витрины без партнёров приходят без поля.
	partnerCatalog: z.array(PartnerProduct).optional(),
	// fd-boutique-01: код ссылки магазина /s/<код> и тариф («premium» —
	// опубликован бутик из конструктора).
	shareCode: z.string().optional(),
	tier: z.string().optional(),
});
export type Storefront = z.infer<typeof Storefront>;

// ── Cart ──────────────────────────────────────────────────────────────────────
export const CartItem = z.object({
	id: z.string(),
	productId: z.string(),
	shopId: z.string(),
	name: z.string(),
	brand: z.string(),
	photo: z.string(),
	variantId: z.string().optional(),
	variantLabel: z.string().optional(),
	customOfferId: z.string().optional(),
	customOfferDescription: z.string().optional(),
	productionEstimate: z.string().optional(),
	options: z.record(z.string(), z.string()).optional(),
	size: z.string(),
	color: z.string(),
	qty: z.number(),
	unitPrice: z.number(),
	oldUnitPrice: z.number().optional(),
	lineTotal: z.number(),
	stock: z.number(),
	/** Минимальный заказ, шт.: минус в корзине ниже не опускает. */
	minOrderQty: z.number().optional(),
	madeToOrder: z.boolean().default(false),
	weightGrams: z.number().optional(),
	lengthCm: z.number().optional(),
	widthCm: z.number().optional(),
	heightCm: z.number().optional(),
	available: z.boolean(),
	/** Причина недоступности позиции: закончилась, ушла на перемодерацию или
	 *  снята с продажи. Приходит только при available: false. */
	unavailableReason: z.enum(["out_of_stock", "moderation", "unpublished"]).optional(),
	/** Выбранные изменения (кастомизация) строки: цены — с сервера, из карточки
	 *  товара. Нужны таблице кастомизации на странице товара, чтобы показать уже
	 *  выбранное. */
	customization: z
		.array(z.object({ name: z.string(), qty: z.number(), unitPrice: z.number(), lineTotal: z.number() }))
		.optional(),
	/** fd-promo-15: доля скидки по промокоду на всю строку, руб. */
	discount: z.number().optional(),
	/** Промокод действует на эту позицию — пометка в корзине. */
	promoApplied: z.boolean().optional(),
	/** fd-promo-03: позиция по цене действующей акции магазина. */
	sellerPromo: z.object({ id: z.string(), title: z.string() }).optional(),
});
export type CartItem = z.infer<typeof CartItem>;

export const Cart = z.object({
	id: z.string(),
	items: z.array(CartItem),
	itemCount: z.number(),
	subtotal: z.number(),
	discount: z.number().default(0),
	promoCode: z.string().optional(),
	/** Тип кода: free_delivery — скидка в доставке (fd-promo-05). */
	promoKind: z.enum(["percent", "fixed", "free_delivery"]).optional(),
	/** Код магазина: id магазина-владельца. */
	promoShopId: z.string().optional(),
	/** Магазины, доставка которых бесплатна по коду. */
	freeDeliveryShopIds: z.array(z.string()).optional(),
	updatedAt: z.string(),
});
export type Cart = z.infer<typeof Cart>;

export type AddCartItemRequest = {
	productId: string;
	variantId?: string;
	size?: string;
	color?: string;
	qty: number;
	/**
	 * Выбранные покупателем изменения товара: только название и количество.
	 * Цену подставляет сервер из карточки — деньги нельзя принимать на слово,
	 * иначе клиент прислал бы вышивку за рубль.
	 */
	customization?: { name: string; qty: number }[];
};

// ── Checkout / orders ───────────────────────────────────────────────────────
export const DeliveryMethod = z.enum(["standard", "express", "pickup"]);
export type DeliveryMethod = z.infer<typeof DeliveryMethod>;

// ── Delivery quotes (СДЭК carrier or flat fallback) ──────────────────────────
// At checkout the buyer picks a delivery option per shop group from a live
// quote. `carrier: "sdek"` carries a tariff code (and may deliver to a pickup
// point); `carrier: "flat"` is the legacy per-method fallback.
// «self» — своя доставка продавца (GET /delivery/self-options), ПЭК/Деловые
// линии шлюз в квоту пока не кладёт, но строгая схема не должна ронять
// оформление, если опция с таким перевозчиком придёт.
// «seller_pickup» — самовывоз из точки продавца (GET /delivery/seller-pickup-options).
export const DeliveryCarrier = z.enum(["sdek", "flat", "self", "pecom", "dellin", "seller_pickup"]);
export type DeliveryCarrier = z.infer<typeof DeliveryCarrier>;
// Перевозчик уже оформленного заказа: кроме СДЭК и плоского тарифа бывает своя
// доставка продавца и ПЭК/Деловые линии (шлюз, 25.09.2026). Выбор в оформлении
// их пока не предлагает, но заказ с таким отправлением не должен пропадать из
// списка покупателя из-за строгой схемы.
export const OrderCarrier = z.enum(["sdek", "flat", "self", "pecom", "dellin", "seller_pickup"]);

export const DeliveryOption = z.object({
	carrier: DeliveryCarrier,
	method: DeliveryMethod.optional(),
	tariffCode: z.number().optional(),
	label: z.string(),
	deliverySum: z.number(),
	deliveryDays: z.string().optional(),
	toPickupPoint: z.boolean().optional(),
	// СДЭК примерка (TRYING_ON) доступна для этой опции — доставка в ПВЗ, где
	// возможна примерочная. Итоговая доступность зависит от конкретного ПВЗ
	// (см. isDressingRoom у пункта выдачи).
	supportsTryOn: z.boolean().optional(),
	// СДЭК частичный выкуп (PART_DELIV) доступен для этой опции.
	supportsPartial: z.boolean().optional(),
	// §8: курьер вне зоны — опция не исчезает, а показывается отключённой.
	unavailable: z.boolean().optional(),
	unavailableReason: z.string().optional(),
});
export type DeliveryOption = z.infer<typeof DeliveryOption>;

export const DeliveryQuoteGroup = z.object({
	shopId: z.string(),
	shopName: z.string(),
	carrier: DeliveryCarrier,
	options: z.array(DeliveryOption),
	courierAvailable: z.boolean().optional(),
	courierUnavailableReason: z.string().optional(),
});
export type DeliveryQuoteGroup = z.infer<typeof DeliveryQuoteGroup>;

export const DeliveryQuote = z.object({ groups: z.array(DeliveryQuoteGroup) });
export type DeliveryQuote = z.infer<typeof DeliveryQuote>;

// Своя доставка продавца в город покупателя (GET /delivery/self-options):
// по группе магазина — предложение (цена со скидкой «бесплатно от», срок) или
// null, если продавец туда сам не возит.
export const SelfDeliveryOffer = z.object({
	shopId: z.string(),
	city: z.string(),
	price: z.number(),
	basePrice: z.number(),
	freeOver: z.number().nullable().optional(),
	daysMin: z.number().nullable().optional(),
	daysMax: z.number().nullable().optional(),
	deliveryDays: z.string().optional(),
	note: z.string().nullable().optional(),
});
export type SelfDeliveryOffer = z.infer<typeof SelfDeliveryOffer>;
export const SelfDeliveryShopOption = z.object({
	shopId: z.string(),
	shopName: z.string(),
	offer: SelfDeliveryOffer.nullable(),
});
export type SelfDeliveryShopOption = z.infer<typeof SelfDeliveryShopOption>;

// Самовывоз от продавца (GET /delivery/seller-pickup-options): точки магазина —
// все, с городом; точки в городе покупателя идут первыми (`inBuyerCity`).
// `available` — есть точка в городе покупателя; нет — витрина предлагает
// выбрать город точки (`cities`).
export const SellerPickupPoint = z.object({
	id: z.string(),
	name: z.string(),
	city: z.string(),
	address: z.string(),
	postalCode: z.string().optional(),
	schedule: z.string(),
	phone: z.string(),
	instructions: z.string(),
	readyAmount: z.number(),
	readyUnit: z.enum(["hours", "days"]),
	readyLabel: z.string(),
	price: z.number(),
	storageDays: z.number(),
	inBuyerCity: z.boolean().optional(),
});
export type SellerPickupPoint = z.infer<typeof SellerPickupPoint>;
export const SellerPickupOffer = z.object({
	shopId: z.string(),
	available: z.boolean(),
	cities: z.array(z.string()),
	minPrice: z.number(),
	points: z.array(SellerPickupPoint),
});
export type SellerPickupOffer = z.infer<typeof SellerPickupOffer>;
export const SellerPickupShopOption = z.object({
	shopId: z.string(),
	shopName: z.string(),
	offer: SellerPickupOffer.nullable(),
});
export type SellerPickupShopOption = z.infer<typeof SellerPickupShopOption>;

// Блок «Доставка» на карточке товара: опции группы (как в оформлении) и
// предложение своей доставки продавца в этот город.
export const ProductDeliveryOptions = DeliveryQuoteGroup.extend({
	city: z.string(),
	self: SelfDeliveryOffer.nullable(),
	// Самовывоз от продавца: города точек и самая низкая стоимость.
	sellerPickup: z.object({ cities: z.array(z.string()), minPrice: z.number() }).nullish(),
});
export type ProductDeliveryOptions = z.infer<typeof ProductDeliveryOptions>;

export type DeliveryQuoteRequest = {
	cartId: string;
	country?: string;
	city: string;
	postalCode?: string;
	// Частичное оформление: квота только по выбранным строкам корзины.
	lineIds?: string[];
};

// The buyer's chosen delivery for one shop group, sent with the order. Mirrors
// the picked option plus the resolved pickup point when the tariff is to-point.
export type DeliverySelection = {
	shopId: string;
	carrier: DeliveryCarrier;
	method?: DeliveryMethod;
	tariffCode?: number;
	tariffName?: string;
	deliveryDays?: string;
	deliverySum: number;
	toPickupPoint?: boolean;
	pvzCode?: string;
	pvzAddress?: string;
	// Покупатель заказал примерку (СДЭК TRYING_ON) в ПВЗ.
	tryOn?: boolean;
	// Покупатель заказал частичный выкуп (СДЭК PART_DELIV).
	partial?: boolean;
	// Самовывоз от продавца: выбранная точка магазина (обязательна).
	pickupPointId?: string;
};

// A carrier pickup point (ПВЗ / постамат) for the checkout picker.
// `isDressingRoom` — есть ли примерочная (условие примерки и частичного выкупа).
export const PickupPoint = z.object({
	code: z.string(),
	name: z.string(),
	address: z.string(),
	cityCode: z.number(),
	city: z.string(),
	longitude: z.number(),
	latitude: z.number(),
	type: z.string(),
	workTime: z.string(),
	isDressingRoom: z.boolean(),
});
export type PickupPoint = z.infer<typeof PickupPoint>;

// A carrier shipment for one shop group of an order — the mutable tracking
// record (status + cdek number fill in asynchronously via the carrier webhook).
export const OrderShipment = z.object({
	id: z.string(),
	orderId: z.string(),
	shopId: z.string(),
	carrier: OrderCarrier,
	sdekUuid: z.string().optional(),
	cdekNumber: z.string().optional(),
	// Трек-номер для покупателя (у ПЭК/ДЛ — свой, у СДЭК совпадает с cdekNumber).
	trackingNumber: z.string().optional(),
	tariffCode: z.number().optional(),
	deliveryPoint: z.string().optional(),
	deliverySum: z.number().optional(),
	state: z.string(),
	statusCode: z.string().optional(),
	statusName: z.string().optional(),
	statusAt: z.string().optional(),
	statusHistory: z.array(
		z.object({
			code: z.string().optional(),
			name: z.string().optional(),
			at: z.string().optional(),
			comment: z.string().optional(),
		}),
	),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type OrderShipment = z.infer<typeof OrderShipment>;

export type ContactInfo = { name: string; email: string; phone: string };
export type ShippingAddress = {
	country: string;
	city: string;
	street: string;
	postalCode: string;
	comment?: string;
	/** Субъект РФ из подсказки DaData; шлюз сводит его к справочнику (fd-analytics-04). */
	region?: string;
	regionFiasId?: string;
};
const PaymentResult = z.object({
	// Optional for back-compat with snapshots written before the provider split.
	provider: z.enum(["robokassa", "cloudpayments", "mock"]).optional(),
	status: z.enum(["pending", "approved", "declined"]),
	transactionId: z.string().optional(),
	brand: z.string(),
	last4: z.string(),
});

// Конфигурация провайдера, которую читает покупательский клиент.
//
// `mode` решает, как платить: "widget" — браузерный виджет CloudPayments
// (нужен publicId, он не секретный), "redirect" — платёжная страница
// Робокассы, ссылку на неё выдаёт POST /orders/:id/payment-url. `mock` —
// офлайн-путь без оплаты вообще.
export const PublicPaymentConfig = z.object({
	provider: z.enum(["robokassa", "cloudpayments", "mock"]),
	mode: z.enum(["widget", "redirect"]).optional(),
	publicId: z.string().optional(),
	currency: z.string(),
	/** Тестовый режим провайдера: показываем покупателю явно. */
	isTest: z.boolean().optional(),
	/** fd-payment-01: провайдер сохраняет карты и платит ими. */
	savedCards: z.boolean().optional(),
	/** fd-order-18: при отмене деньги возвращаются на карту через API провайдера. */
	refunds: z.boolean().optional(),
	// fd-payment-04: ставка комиссии площадки для показа строки комиссии на
	// витрине. Не влияет на суммы заказа — комиссию несёт продавец.
	commissionRate: z.number().optional(),
});
export type PublicPaymentConfig = z.infer<typeof PublicPaymentConfig>;

/** Ответ POST /orders/:id/payment-url — адрес платёжной страницы провайдера. */
export const PaymentUrl = z.object({ url: z.string().url() });

const OrderItem = z.object({
	productId: z.string(),
	name: z.string(),
	brand: z.string(),
	photo: z.string(),
	variantId: z.string().optional(),
	variantLabel: z.string().optional(),
	customOfferId: z.string().optional(),
	customOfferDescription: z.string().optional(),
	productionEstimate: z.string().optional(),
	options: z.record(z.string(), z.string()).optional(),
	size: z.string(),
	color: z.string(),
	qty: z.number(),
	unitPrice: z.number(),
	oldUnitPrice: z.number().optional(),
	lineTotal: z.number(),
	weightGrams: z.number().optional(),
	lengthCm: z.number().optional(),
	widthCm: z.number().optional(),
	heightCm: z.number().optional(),
	/** fd-promo-15: доля скидки по промокоду на строку, руб. */
	discount: z.number().optional(),
	promoApplied: z.boolean().optional(),
	sellerPromo: z.object({ id: z.string(), title: z.string() }).optional(),
});

export const OrderGroup = z.object({
	shopId: z.string(),
	shopName: z.string(),
	shopSlug: z.string(),
	items: z.array(OrderItem),
	subtotal: z.number(),
	deliveryFee: z.number(),
	// The chosen carrier delivery for this group (absent on legacy orders).
	shipment: z
		.object({
			carrier: OrderCarrier,
			method: DeliveryMethod.optional(),
			tariffCode: z.number().optional(),
			tariffName: z.string().optional(),
			deliveryDays: z.string().optional(),
			toPickupPoint: z.boolean().optional(),
			pvzCode: z.string().optional(),
			pvzAddress: z.string().optional(),
			pvzWorkTime: z.string().optional(),
			// Самовывоз от продавца: снимок точки на момент оформления.
			pickupPoint: SellerPickupPoint.omit({ inBuyerCity: true }).optional(),
		})
		.optional(),
	// Самовывоз от продавца: статус выдачи, «хранится до» и код получения.
	sellerPickup: z
		.object({
			status: z.enum(["new", "ready", "issued", "expired"]),
			readyAt: z.string().optional(),
			storageUntil: z.string().optional(),
			handedAt: z.string().optional(),
			code: z.string().optional(),
		})
		.optional(),
	/** Скидка по промокоду в группе. */
	discount: z.number().optional(),
	/** fd-promo-05: доставка, обнулённая кодом. */
	deliveryDiscount: z.number().optional(),
});
export type OrderGroup = z.infer<typeof OrderGroup>;

const OrderStatus = z.enum([
	"pending",
	"declined",
	"paid",
	"processing",
	"new",
	"confirmed",
	"in_work",
	"ready_to_ship",
	"shipped",
	"in_transit",
	// Ждёт покупателя: в пункте выдачи или в точке самовывоза продавца.
	"awaiting_pickup",
	"delivered",
	"completed",
	"cancelled",
	"return_requested",
	"dispute_open",
	"archived",
	// §2: «Ждут оплаты» — добавлен аддитивно вслед за общим контрактом.
	"awaiting_payment",
]);

const OrderStatusEvent = z.object({
	id: z.string(),
	orderId: z.string(),
	actorType: z.enum(["system", "buyer", "seller", "admin", "carrier"]),
	actorId: z.string().optional(),
	oldStatus: z.string(),
	newStatus: z.string(),
	reason: z.string().optional(),
	meta: z.record(z.string(), z.unknown()),
	createdAt: z.string(),
});

const OrderReview = z.object({
	id: z.string(),
	orderId: z.string(),
	buyerUserId: z.string().optional(),
	rating: z.number(),
	text: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

const OrderReturnRequest = z.object({
	id: z.string(),
	orderId: z.string(),
	buyerUserId: z.string().optional(),
	kind: z.enum(["return", "dispute"]),
	reason: z.string(),
	status: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export const Order = z.object({
	id: z.string(),
	// Короткий номер заказа (123456, миграция 285); id остаётся ключом адресов и оплаты.
	number: z.number().nullish(),
	status: OrderStatus,
	contact: z.object({ name: z.string(), email: z.string(), phone: z.string() }),
	address: z.object({
		country: z.string(),
		city: z.string(),
		street: z.string(),
		postalCode: z.string(),
		comment: z.string().optional(),
	}),
	delivery: DeliveryMethod,
	payment: PaymentResult,
	groups: z.array(OrderGroup),
	subtotal: z.number(),
	discount: z.number().default(0),
	promoCode: z.string().optional(),
	promoKind: z.enum(["percent", "fixed", "free_delivery"]).optional(),
	/** fd-promo-05: обнулённая кодом доставка. */
	deliveryDiscount: z.number().optional(),
	/** fd-promo-16: раздельное оформление — заказы одного оформления. */
	checkoutId: z.string().optional(),
	checkoutOrders: z
		.array(z.object({ id: z.string(), number: z.number().optional(), total: z.number(), promo: z.boolean() }))
		.optional(),
	deliveryTotal: z.number(),
	total: z.number(),
	itemCount: z.number(),
	createdAt: z.string(),
	readyToShipAt: z.string().optional(),
	trackingNumber: z.string().optional(),
	cancelledAt: z.string().optional(),
	completedAt: z.string().optional(),
	statusHistory: z.array(OrderStatusEvent).optional(),
	review: OrderReview.optional(),
	returnRequests: z.array(OrderReturnRequest).optional(),
	refund: z
		.object({
			// "refunded" — деньги вернулись в PSP, "external_settlement" — возврат
			// вне системы, "mock_recorded" — заказы, отменённые до 17.09.2026.
			status: z.enum(["mock_recorded", "refunded", "external_settlement", "pending"]),
			provider: z.string().optional(),
			transactionId: z.string().optional(),
			amount: z.number().optional(),
			reason: z.string().optional(),
			createdAt: z.string(),
		})
		.optional(),
	// fd-payment-03: сумма, оплаченная внутренним балансом (вне PSP-платежа).
	balanceApplied: z.number().optional(),
	// fd-payment-03: сумма к оплате провайдером (total − balanceApplied).
	payableTotal: z.number().optional(),
	// fd-payment-02: 'invoice' — заказ на оплату по счёту (ЮЛ).
	paymentMethod: z.enum(["invoice"]).optional(),
	// Оплата при получении: 'cod' — деньги собирает СДЭК при выдаче, площадка
	// не списывает их при оформлении. Отсутствует/'prepaid' у обычных заказов.
	paymentMode: z.enum(["prepaid", "cod"]).optional(),
	paidAt: z.string().optional(),
	// Код получения и «забрать до» (ПВЗ и самовывоз от продавца).
	pickupCode: z.string().optional(),
	pickupExpiresAt: z.string().optional(),
});
export type Order = z.infer<typeof Order>;

export type CreateOrderRequest = {
	cartId: string;
	contact: ContactInfo;
	address: ShippingAddress;
	// One delivery selection per shop group in the cart (from the quote).
	selections: DeliverySelection[];
	// fd-payment-03: оплатить (частично/полностью) внутренним балансом.
	useBalance?: boolean;
	balanceAmount?: number;
	// fd-payment-02: 'invoice' — оформить заказ на оплату по счёту (ЮЛ).
	paymentMethod?: "invoice";
	// Оплата при получении (СДЭК наложенный платёж). По умолчанию prepaid.
	paymentMode?: "prepaid" | "cod";
	// «Как покупатель нашёл товар» — колонка таблицы продаж в кабинете
	// продавца. Витрина знает путь к товару, заказ — нет; см. lib/discovery-source.
	discoverySource?: "search" | "catalog" | "promotion" | "ads" | "recommendations" | "other" | "inspiration_feed";
	// Частичное оформление: только выбранные строки корзины (cart.items[].id).
	// Без поля — вся корзина. Скидку промокода шлюз в частичный заказ не переносит.
	lineIds?: string[];
	// fd-promo-16: товары со скидкой по промокоду — отдельным заказом (своя оплата).
	splitPromo?: boolean;
	// Цифровой UGC-артикул из памяти устройства (30 дней, lib/ugc-attribution):
	// заказ засчитывается артикулу и партнёру. Без поля шлюз ищет переход сам.
	ugcArticleCode?: string;
	// fd-payment-01: согласие «Сохранить карту» — карта ляжет в способы оплаты
	// после подтверждённой оплаты (токен провайдера и маска).
	saveCard?: boolean;
};

// ── Settings (delivery pricing) ───────────────────────────────────────────────
// Per-shop delivery fees + free-delivery threshold. Served by the gateway
// (editable from admin) — the storefront fetches it to price checkout instead
// of hardcoding the numbers.
export const DeliveryConfig = z.object({
	fees: z.object({ standard: z.number(), express: z.number(), pickup: z.number() }),
	freeOver: z.number(),
	updatedAt: z.string(),
});
export type DeliveryConfig = z.infer<typeof DeliveryConfig>;

// ── Geo / locale (location picker) ────────────────────────────────────────────
// Supported delivery countries (with cities + default currency) and display
// currencies, both served by the gateway so the location modal isn't hardcoded.
export const Country = z.object({
	code: z.string(),
	name: z.string(),
	currency: z.string(),
	cities: z.array(z.string()),
	// Soft on/off in the geo SSOT; the public list only returns enabled ones,
	// so older payloads without the field are treated as enabled.
	enabled: z.boolean().optional().default(true),
});
export type Country = z.infer<typeof Country>;

export const Currency = z.object({
	code: z.string(),
	symbol: z.string(),
	name: z.string(),
	// RUB→target multiplier, applied for display only (RUB stays the base).
	rate: z.number(),
	symbolBefore: z.boolean(),
});
export type Currency = z.infer<typeof Currency>;

// A normalized address suggestion for the checkout autocomplete (DaData-backed).
// Selecting one backfills the structured shipping fields + the СДЭК destination.
export const AddressSuggestion = z.object({
	value: z.string(),
	country: z.string(),
	city: z.string(),
	street: z.string(),
	postalCode: z.string(),
});
export type AddressSuggestion = z.infer<typeof AddressSuggestion>;

export const CitySuggestion = z.object({
	value: z.string(),
	country: z.string(),
	city: z.string(),
	region: z.string(),
	postalCode: z.string(),
});
export type CitySuggestion = z.infer<typeof CitySuggestion>;

// ── Inspiration feed (лента вдохновения) ──────────────────────────────────────
// Pinterest-style curated product collections (discovery.001/002/003 + the
// `collections` BRD). Each collection has a cover image with shoppable
// hotspots — clickable pins positioned over the image that link to a product.
export const FeedHotspot = z.object({
	// Position on the cover image as a percentage of width/height (0–100).
	x: z.number(),
	y: z.number(),
	productId: z.string(),
	productName: z.string(),
	brand: z.string(),
	// Price in base currency (RUB); converted for display via useFormatPrice.
	price: z.number(),
	photo: z.string(),
});
export type FeedHotspot = z.infer<typeof FeedHotspot>;

// One picture in a collection, with its own shoppable hotspots. Editorial
// templates have a single image; a seller-authored "idea" has one per uploaded
// picture, each pinned independently.
export const FeedImage = z.object({
	url: z.string(),
	hotspots: z.array(FeedHotspot),
});
export type FeedImage = z.infer<typeof FeedImage>;

export const FeedCollectionType = z.enum(["inspiration", "seasonal", "collaboration"]);
export type FeedCollectionType = z.infer<typeof FeedCollectionType>;

export const FeedCollection = z.object({
	id: z.string(),
	slug: z.string(),
	title: z.string(),
	description: z.string(),
	type: FeedCollectionType,
	// First picture's URL; mirrors images[0].url (the feed wall renders a cover).
	cover: z.string(),
	author: z.string(),
	productCount: z.number(),
	// Сумма цен товаров коллекции — для бейджа «N ₽» на карточке идеи.
	totalPrice: z.number().default(0),
	// Every product in the collection; `hotspots` is the subset pinned on cover.
	productIds: z.array(z.string()),
	// Hotspots on the cover image (mirrors images[0].hotspots).
	hotspots: z.array(FeedHotspot),
	// The full picture gallery. A single entry for editorial templates; several
	// for a seller idea. Defaults to a cover-only gallery for older payloads.
	images: z.array(FeedImage).default([]),
	/**
	 * Корневые категории аудитории, к которым относится подборка (корни дерева
	 * с полом «женский / мужской / детский»). Гейтвей выводит их из категорий товаров
	 * подборки. Пусто — подборка не привязана к аудитории (товары для дома,
	 * сборные идеи), такие показываем в любом сегменте.
	 */
	segments: z.array(z.string()).default([]),
});
export type FeedCollection = z.infer<typeof FeedCollection>;

export const ProductCollectionParticipation = z.object({
	id: z.string(),
	slug: z.string(),
	title: z.string(),
	description: z.string(),
	type: FeedCollectionType,
	cover: z.string(),
	author: z.string(),
	productCount: z.number(),
	productIds: z.array(z.string()),
	source: z.enum(["seller_idea", "seller_collection", "buyer_collection", "editorial"]),
});
export type ProductCollectionParticipation = z.infer<typeof ProductCollectionParticipation>;

// ── Unified home feed (fd-feed-01) ─────────────────────────────────────────
// Проводная форма GET /feed/blocks?placement=home&segment=… . Один контракт на
// веб и приложение (см. packages/contracts/src/feed-blocks.ts): админ ведёт
// упорядоченный список типизированных блоков, клиент рендерит их сверху вниз.

// Ось аудитории. Совпадает с корнями-аудиториями дерева категорий (по полу корня).
export const FeedSegment = z.enum(["womens", "mens", "kids"]);
export type FeedSegment = z.infer<typeof FeedSegment>;

// Отдел редакционной ленты: «Одежда» (по полу), «Красота» и «Дом» — корни
// дерева (см. lib/category-tree.ts, lib/home-feed.ts feedDepartmentOf).
export const FeedDepartment = z.enum(["clothes", "home", "beauty"]);
export type FeedDepartment = z.infer<typeof FeedDepartment>;

export const FeedBlockKind = z.enum([
	"campaign",
	"ideaRail",
	"categoryRow",
	"collectionMosaic",
	"productRail",
	"photoBanner",
	"personalFeed",
]);
export type FeedBlockKind = z.infer<typeof FeedBlockKind>;

// Плитка (categoryRow / collectionMosaic / ideaRail).
export const FeedTileWire = z.object({
	id: z.string(),
	imageUrl: z.string(),
	label: z.string().optional(),
	categoryId: z.string().optional(),
	collectionSlug: z.string().optional(),
});
export type FeedTileWire = z.infer<typeof FeedTileWire>;

// Слайд (campaign / photoBanner).
export const FeedSlideWire = z.object({
	id: z.string(),
	imageUrl: z.string(),
	title: z.string().optional(),
	description: z.string().optional(),
	categoryId: z.string().optional(),
	collectionSlug: z.string().optional(),
});
export type FeedSlideWire = z.infer<typeof FeedSlideWire>;

// Один блок выдачи. Неизвестные поля игнорируем — гейтвей может расширять
// контракт, не ломая клиент; неизвестные `kind` клиент пропускает при рендере.
export const FeedBlockWire = z.object({
	id: z.string(),
	kind: FeedBlockKind,
	title: z.string().optional(),
	description: z.string().optional(),
	action: z
		.object({
			kind: z.enum(["link", "chevron"]).optional(),
			label: z.string().optional(),
		})
		.optional(),
	items: z.array(FeedTileWire).optional(),
	slides: z.array(FeedSlideWire).optional(),
	productIds: z.array(z.string()).optional(),
});
export type FeedBlockWire = z.infer<typeof FeedBlockWire>;

// ── Buyer account / cabinet ────────────────────────────────────────────────
export const UserRole = z.enum([
	"buyer",
	"seller",
	"manufacturer",
	"verified_seller",
	"shop-owner",
	"shop-manager",
	"shop-product-manager",
	"shop-order-manager",
	"shop-support",
	"admin",
]);
export type UserRole = z.infer<typeof UserRole>;

export const OnboardingStatus = z.enum([
	"register_pending",
	"survey_pending",
	"profile_pending",
	"moderation_pending",
	"moderation_passed",
	"active",
]);
export type OnboardingStatus = z.infer<typeof OnboardingStatus>;

export const User = z.object({
	id: z.string(),
	email: z.string(),
	phone: z.string(),
	name: z.string(),
	shopName: z.string().optional(),
	shopId: z.string().optional(),
	role: UserRole,
	onboardingStatus: OnboardingStatus,
	avatar: z.string().optional(),
});
export type User = z.infer<typeof User>;

export const BuyerProfile = z.object({
	userId: z.string(),
	name: z.string(),
	email: z.string().optional(),
	phone: z.string(),
	birthDate: z.string().optional(),
	// §5 расширенный профиль (миграция 063): раздельное ФИО, пол, аватар.
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	patronymic: z.string().optional(),
	gender: z.enum(["female", "male"]).optional(),
	avatarUrl: z.string().optional(),
	// Регион покупателя (миграция 072): выбранный/определённый город и страна.
	city: z.string().optional(),
	countryCode: z.string().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type BuyerProfile = z.infer<typeof BuyerProfile>;

export const BuyerMe = z.object({ user: User, profile: BuyerProfile });
export type BuyerMe = z.infer<typeof BuyerMe>;

export type BuyerAuthStartRequest = { phone: string; deviceName?: string };
export const BuyerAuthStartResponse = z.object({
	sessionId: z.string(),
	maskedTarget: z.string(),
	expiresAt: z.string(),
});
export type BuyerAuthStartResponse = z.infer<typeof BuyerAuthStartResponse>;

export type BuyerAuthVerifyRequest = {
	sessionId: string;
	code: string;
	deviceName?: string;
	/** Код партнёрской ссылки (?promo=), запомненный на устройстве. */
	promo?: string;
};
export const BuyerAuthVerifyResponse = z.discriminatedUnion("success", [
	z.object({
		success: z.literal(true),
		token: z.string(),
		user: User,
		profile: BuyerProfile,
		/** Аккаунт создан этим входом (цель «Регистрация» в Метрике). */
		isNewUser: z.boolean().optional(),
	}),
	z.object({ success: z.literal(false), attemptsLeft: z.number(), fallback: z.literal("restart").optional() }),
]);
export type BuyerAuthVerifyResponse = z.infer<typeof BuyerAuthVerifyResponse>;

// Вход через внешние аккаунты: Яндекс ID и VK ID (ТЗ, п. 1.2). Шлюз отдаёт
// только подключённые провайдеры; без ключей список пуст и кнопок нет.
export const OAuthProviderId = z.enum(["yandex", "vk"]);
export type OAuthProviderId = z.infer<typeof OAuthProviderId>;
export const OAuthProviderInfo = z.object({ id: OAuthProviderId, title: z.string() });
export type OAuthProviderInfo = z.infer<typeof OAuthProviderInfo>;
export const OAuthIdentity = z.object({
	provider: OAuthProviderId,
	title: z.string(),
	email: z.string().optional(),
	displayName: z.string().optional(),
	linkedAt: z.string(),
});
export type OAuthIdentity = z.infer<typeof OAuthIdentity>;
export const OAuthExchangeResult = z.discriminatedUnion("status", [
	z.object({
		status: z.literal("signed_in"),
		token: z.string(),
		user: User,
		profile: BuyerProfile,
		isNewUser: z.boolean().optional(),
		provider: OAuthProviderId,
		returnTo: z.string().optional(),
	}),
	z.object({
		status: z.literal("need_phone"),
		ticket: z.string(),
		provider: OAuthProviderId,
		displayName: z.string().optional(),
		returnTo: z.string().optional(),
	}),
]);
export type OAuthExchangeResult = z.infer<typeof OAuthExchangeResult>;

// Удаление аккаунта покупателя: что мешает, SMS-код, подтверждение.
export const AccountDeletionBlocker = z.object({
	kind: z.string(),
	message: z.string(),
	count: z.number().optional(),
	amount: z.number().optional(),
	orderNumbers: z.array(z.string()).optional(),
});
export type AccountDeletionBlocker = z.infer<typeof AccountDeletionBlocker>;
export const AccountDeletionCheck = z.object({ canDelete: z.boolean(), blockers: z.array(AccountDeletionBlocker) });
export type AccountDeletionCheck = z.infer<typeof AccountDeletionCheck>;
export const AccountDeletionResult = z.union([
	z.object({ deleted: z.literal(true) }).passthrough(),
	z.object({ deleted: z.literal(false), attemptsLeft: z.number() }),
]);
export type AccountDeletionResult = z.infer<typeof AccountDeletionResult>;

export type BuyerProfilePatch = { name?: string; email?: string; birthDate?: string };
// §5 расширенный патч (PATCH /buyer/profile): раздельное ФИО и пол.
export type BuyerProfileExtPatch = BuyerProfilePatch & {
	gender?: "female" | "male";
	firstName?: string;
	lastName?: string;
	patronymic?: string;
	city?: string;
	countryCode?: string;
};

// Обратный геокод для автоопределения города при первом входе (GET /geo/reverse).
export const GeoReverse = z.object({
	city: z.string().optional(),
	region: z.string().optional(),
	country: z.string().optional(),
});
export type GeoReverse = z.infer<typeof GeoReverse>;
// §5 смена номера телефона: 4 шага (код на текущий → новый номер → код на новый).
export const PhoneChangeStart = z.object({ sessionId: z.string(), maskedTarget: z.string(), expiresAt: z.string() });
export type PhoneChangeStart = z.infer<typeof PhoneChangeStart>;
export const PhoneChangeVerify = z.object({ success: z.boolean(), attemptsLeft: z.number() });
export type PhoneChangeVerify = z.infer<typeof PhoneChangeVerify>;
export const PhoneChangeNew = z.object({ maskedTarget: z.string(), expiresAt: z.string() });
export type PhoneChangeNew = z.infer<typeof PhoneChangeNew>;
// §3 справочник банков СБП (GET /payments/sbp/banks).
export const SbpBank = z.object({ id: z.string(), name: z.string(), logoUrl: z.string() });
export type SbpBank = z.infer<typeof SbpBank>;

export const BuyerSession = z.object({
	id: z.string(),
	deviceName: z.string().optional(),
	userAgent: z.string().optional(),
	ipAddress: z.string().optional(),
	createdAt: z.string(),
	lastSeenAt: z.string().optional(),
	current: z.boolean(),
});
export type BuyerSession = z.infer<typeof BuyerSession>;

export const BuyerAddress = z.object({
	id: z.string(),
	label: z.string(),
	recipient: z.string(),
	phone: z.string(),
	country: z.string(),
	city: z.string(),
	street: z.string(),
	postalCode: z.string(),
	comment: z.string(),
	isDefault: z.boolean(),
	// Детали курьерского адреса (миграция 069): квартира, этаж, подъезд, домофон.
	apartment: z.string().optional(),
	floor: z.string().optional(),
	entrance: z.string().optional(),
	intercom: z.string().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type BuyerAddress = z.infer<typeof BuyerAddress>;
export type BuyerAddressInput = Omit<BuyerAddress, "id" | "createdAt" | "updatedAt">;

// Оформление заказа (макет «5.1 Оформление заказа-2», миграция 069):
// сохранённые получатели и ранее выбранные пункты выдачи.
export const BuyerRecipient = z.object({
	id: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	phone: z.string(),
	isDefault: z.boolean(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type BuyerRecipient = z.infer<typeof BuyerRecipient>;
export type BuyerRecipientInput = { firstName: string; lastName?: string; phone: string; isDefault?: boolean };

export const SavedPickupPoint = z.object({
	carrier: z.string(),
	code: z.string(),
	name: z.string(),
	address: z.string(),
	city: z.string(),
	workTime: z.string(),
	isDressingRoom: z.boolean(),
	latitude: z.number().optional(),
	longitude: z.number().optional(),
	lastUsedAt: z.string(),
});
export type SavedPickupPoint = z.infer<typeof SavedPickupPoint>;
export type SavedPickupPointInput = Omit<SavedPickupPoint, "lastUsedAt" | "name" | "city" | "workTime" | "isDressingRoom"> &
	Partial<Pick<SavedPickupPoint, "name" | "city" | "workTime" | "isDressingRoom">>;

// ИНН → организация («Счёт на оплату»), DaData через шлюз.
export const BuyerCompanySuggestion = z.object({
	inn: z.string(),
	shortName: z.string(),
	fullName: z.string(),
	legalAddress: z.string(),
	kpp: z.string().optional(),
	ogrn: z.string().optional(),
});
export type BuyerCompanySuggestion = z.infer<typeof BuyerCompanySuggestion>;

export const BuyerPaymentMethod = z.object({
	id: z.string(),
	provider: z.string(),
	providerToken: z.string(),
	brand: z.string(),
	last4: z.string(),
	label: z.string(),
	expiresAt: z.string(),
	isDefault: z.boolean(),
	createdAt: z.string(),
	updatedAt: z.string(),
	// fd-payment-01: карта сохранена через платёжную систему — вид токена
	// провайдера и маска номера; `chargeable` — ею можно оплатить заказ.
	tokenKind: z.string().optional(),
	cardMask: z.string().optional(),
	chargeable: z.boolean().optional(),
});
export type BuyerPaymentMethod = z.infer<typeof BuyerPaymentMethod>;
export type BuyerPaymentMethodInput = Omit<
	BuyerPaymentMethod,
	"id" | "createdAt" | "updatedAt" | "tokenKind" | "cardMask" | "chargeable"
>;

/**
 * Итог оплаты сохранённой картой (fd-payment-01): заказ оплачен, нужна
 * страница провайдера (Робокасса просит CVC), подтверждение 3-D Secure или
 * отказ — тогда покупатель платит картой заново.
 */
export const SavedCardPayResult = z.discriminatedUnion("status", [
	z.object({ status: z.literal("paid") }).passthrough(),
	z.object({ status: z.literal("redirect"), url: z.string() }),
	z.object({ status: z.literal("3ds"), acsUrl: z.string(), paReq: z.string(), md: z.string(), termUrl: z.string() }),
	z.object({ status: z.literal("declined"), reason: z.string() }),
]);
export type SavedCardPayResult = z.infer<typeof SavedCardPayResult>;
// fd-payment-01: правка способа оплаты — переименование и/или «сделать основным».
export type BuyerPaymentMethodPatch = { label?: string; isDefault?: boolean };

// ── Внутренний баланс покупателя + леджер (fd-payment-03) ─────────────────────
export const BuyerBalance = z.object({
	userId: z.string(),
	amount: z.number(),
	currency: z.string(),
	updatedAt: z.string(),
});
export type BuyerBalance = z.infer<typeof BuyerBalance>;

export const LedgerKind = z.enum(["topup", "spend", "refund", "adjust"]);
export type LedgerKind = z.infer<typeof LedgerKind>;

export const LedgerEntry = z.object({
	id: z.string(),
	userId: z.string(),
	delta: z.number(),
	kind: LedgerKind,
	refType: z.string().optional(),
	refId: z.string().optional(),
	balanceAfter: z.number(),
	comment: z.string(),
	createdAt: z.string(),
});
export type LedgerEntry = z.infer<typeof LedgerEntry>;

export const BalanceTopup = z.object({
	id: z.string(),
	userId: z.string(),
	amount: z.number(),
	currency: z.string(),
	status: z.enum(["pending", "paid", "declined"]),
	provider: z.string(),
	transactionId: z.string().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type BalanceTopup = z.infer<typeof BalanceTopup>;

export const TopupConfirmResult = z.object({
	topup: BalanceTopup,
	balance: BuyerBalance,
});
export type TopupConfirmResult = z.infer<typeof TopupConfirmResult>;

// ── Реквизиты ЮЛ покупателя для оплаты по счёту (fd-payment-02) ───────────────
export const BillingProfile = z.object({
	userId: z.string(),
	companyName: z.string(),
	inn: z.string(),
	kpp: z.string(),
	ogrn: z.string(),
	legalAddress: z.string(),
	bankName: z.string(),
	bankAccount: z.string(),
	corrAccount: z.string(),
	bik: z.string(),
	contactName: z.string(),
	contactEmail: z.string(),
	contactPhone: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type BillingProfile = z.infer<typeof BillingProfile>;

export type BillingProfileInput = {
	companyName: string;
	inn: string;
	kpp?: string;
	ogrn?: string;
	legalAddress?: string;
	bankName?: string;
	bankAccount?: string;
	corrAccount?: string;
	bik?: string;
	contactName?: string;
	contactEmail?: string;
	contactPhone?: string;
};

// ── Счёт по заказу «оплата по счёту» (fd-payment-02) ─────────────────────────
export const InvoicePlatformRequisites = z.object({
	companyName: z.string(),
	inn: z.string(),
	kpp: z.string(),
	ogrn: z.string(),
	legalAddress: z.string(),
	bankName: z.string(),
	bankAccount: z.string(),
	corrAccount: z.string(),
	bik: z.string(),
});
export type InvoicePlatformRequisites = z.infer<typeof InvoicePlatformRequisites>;

export const InvoiceItem = z.object({
	name: z.string(),
	shopName: z.string(),
	qty: z.number(),
	unitPrice: z.number(),
	lineTotal: z.number(),
});
export type InvoiceItem = z.infer<typeof InvoiceItem>;

export const Invoice = z.object({
	orderId: z.string(),
	number: z.string(),
	issuedAt: z.string(),
	currency: z.string(),
	buyer: BillingProfile,
	platform: InvoicePlatformRequisites,
	items: z.array(InvoiceItem),
	subtotal: z.number(),
	deliveryTotal: z.number(),
	total: z.number(),
});
export type Invoice = z.infer<typeof Invoice>;

export const BuyerMessage = z.object({
	id: z.string(),
	conversationId: z.string().optional(),
	productId: z.string(),
	productName: z.string(),
	shopId: z.string(),
	contact: z.string(),
	message: z.string(),
	authorRole: z.enum(["buyer", "seller"]).optional(),
	createdAt: z.string(),
});
export type BuyerMessage = z.infer<typeof BuyerMessage>;

// ── Engagement / likes / product messages ─────────────────────────────────
export const LikeState = z.object({
	productIds: z.array(z.string()),
	shopIds: z.array(z.string()),
	ideaIds: z.array(z.string()),
	// Размер, выбранный при добавлении товара в избранное (productId → size).
	// Опционально: старый бэк/гостевой ответ может не отдавать поле.
	productSizes: z.record(z.string(), z.string()).optional(),
});
export type LikeState = z.infer<typeof LikeState>;

/** Ответ `POST /buyer/device/attach`: состояние аккаунта после того, как
 *  гостевые корзина, избранное и просмотры устройства в него переехали. */
export const DeviceAttachResult = z.object({
	cart: Cart.nullable(),
	likes: LikeState,
	viewsImported: z.number().int().nonnegative().default(0),
});
export type DeviceAttachResult = z.infer<typeof DeviceAttachResult>;

export const LikeTargetResult = z.object({ liked: z.boolean(), likes: z.number() });
export type LikeTargetResult = z.infer<typeof LikeTargetResult>;

// «Скрыть идею» (замечание Айталы A-26). Шлюз хранит скрытие по гостевому
// clientId или по аккаунту и отдаёт ссылки на идеи так же, как лайки: `slug || id`.
export const HiddenIdeaState = z.object({ ids: z.array(z.string()) });
export type HiddenIdeaState = z.infer<typeof HiddenIdeaState>;
export const HiddenTargetResult = z.object({ hidden: z.boolean() });
export type HiddenTargetResult = z.infer<typeof HiddenTargetResult>;

export const PaginatedLikeIds = z.object({
	ids: z.array(z.string()),
	total: z.number(),
	page: z.number(),
	pageSize: z.number(),
});
export type PaginatedLikeIds = z.infer<typeof PaginatedLikeIds>;

export const ProductMessage = z.object({
	id: z.string(),
	productId: z.string(),
	shopId: z.string(),
	contact: z.string(),
	message: z.string(),
	conversationId: z.string().optional(),
	createdAt: z.string(),
});
export type ProductMessage = z.infer<typeof ProductMessage>;

export const ProductManufacturingRequest = z.object({
	id: z.string(),
	productId: z.string(),
	shopId: z.string(),
	clientId: z.string().optional(),
	buyerUserId: z.string().optional(),
	contact: z.string(),
	message: z.string(),
	customization: z.object({
		productName: z.string(),
		sellerName: z.string().optional(),
		options: z.array(z.object({ name: z.string(), minPrice: z.number(), photo: z.string().optional() })),
		selectedOptions: z.array(z.string()).optional(),
	}),
	status: z.literal("draft"),
	conversationId: z.string().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type ProductManufacturingRequest = z.infer<typeof ProductManufacturingRequest>;

export const ChatMessage = z.object({
	id: z.string(),
	conversationId: z.string(),
	authorRole: z.enum(["buyer", "seller"]),
	authorUserId: z.string().optional(),
	body: z.string(),
	// Фото сообщения (веб «Чаты»: «+» в композере) — всегда массив.
	imageUris: z.array(z.string()).optional(),
	// Вложения шлюза. Витрине нужна карточка товара (`kind: "product"`) —
	// её шлюз сам прикрепляет к первому сообщению треда по товару.
	attachments: z
		.array(
			z.object({
				kind: z.string(),
				productId: z.string().optional(),
				productName: z.string().optional(),
				productPhoto: z.string().optional(),
				priceLabel: z.string().optional(),
				// `kind: "customization"` — строки брифа (индивидуальный заказ с витрины).
				rows: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
			}),
		)
		.optional(),
	// Автомодерация шлюза скрыла в тексте контакты или ссылку (fd-support-04).
	masked: z.boolean().optional(),
	createdAt: z.string(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

export const ChatOffer = z.object({
	id: z.string(),
	conversationId: z.string(),
	shopId: z.string(),
	buyerUserId: z.string(),
	productId: z.string().optional(),
	productName: z.string().optional(),
	productPhoto: z.string().optional(),
	manufacturingRequestId: z.string().optional(),
	description: z.string(),
	quantity: z.number(),
	unitPrice: z.number(),
	productionEstimate: z.string(),
	selectedOptions: z.array(z.string()),
	status: z.enum(["active", "accepted", "cancelled", "expired"]),
	expiresAt: z.string().optional(),
	acceptedAt: z.string().optional(),
	cartId: z.string().optional(),
	cartItemId: z.string().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type ChatOffer = z.infer<typeof ChatOffer>;

export const ChatConversation = z.object({
	id: z.string(),
	shopId: z.string(),
	shopName: z.string().optional(),
	buyerUserId: z.string(),
	buyerName: z.string().optional(),
	clientId: z.string().optional(),
	productId: z.string().optional(),
	productName: z.string().optional(),
	manufacturingRequestId: z.string().optional(),
	subject: z.string(),
	context: z.record(z.string(), z.unknown()),
	lastMessageAt: z.string(),
	lastMessagePreview: z.string().optional(),
	unreadCount: z.number(),
	// Веб «Чаты»: вкладка «Завершенные», закрепление, витрина магазина.
	isCompleted: z.boolean().optional(),
	isArchived: z.boolean().optional(),
	isPinned: z.boolean().optional(),
	shopSlug: z.string().optional(),
	shopLogo: z.string().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
	messages: z.array(ChatMessage).optional(),
	offers: z.array(ChatOffer).optional(),
});
export type ChatConversation = z.infer<typeof ChatConversation>;
// Ответы действий «Чатов»: пачка (закрепить/удалить) и загрузка фото.
export const ChatBulkResult = z.object({ updated: z.number() });
export const ChatUpload = z.object({ url: z.string() });

// ── The api surface ──────────────────────────────────────────────────────────
/**
 * Событие витрины: что покупатель увидел и сделал по дороге к заказу.
 *
 * Имена ровно те, что принимает шлюз (`DiscoveryEventType`):
 * `product_impression` — карточка появилась в окне просмотра выдачи,
 * `product_view` — открыта страница товара, `cart_add` — товар положен в
 * корзину. Метрика воронки «Обзора» считается по ним, поэтому переименовывать
 * их на витрине нельзя: это один словарь на площадку.
 */
export type DiscoveryEventInput = {
	eventType: "feed_view" | "collection_view" | "product_impression" | "product_view" | "cart_add";
	productId?: string;
	collectionId?: string;
	collectionSlug?: string;
	metadata?: Record<string, unknown>;
};

/** Пачка событий одного анонимного клиента. */
export type DiscoveryEventBatch = {
	clientId?: string;
	events: DiscoveryEventInput[];
};

export type Api = {
	// Веб-кабинет покупателя: история, покупки, возвраты, отзывы (./cabinet.ts).
	cabinet: CabinetApi;
	// Акции: горящие, таймер товара, календарь и подписка (П3, ./promos.ts).
	promos: import("./promos").PromosApi;
	// Личные «Образы» покупателя (fd-discovery-04, ./looks.ts).
	looks: import("./looks").LooksApi;
	// Цифровые UGC-артикулы и Единый партнёрский кабинет (./ugc.ts, ТЗ 31.10.2026).
	ugc: import("./ugc").UgcApi;
	products: {
		list(query: ProductQuery): Promise<ProductPage>;
		get(id: string): Promise<Product>;
		byIds(ids: string[]): Promise<Product[]>;
		facets(query: { shopId?: string; search?: string; categoryId?: string }): Promise<ProductFacets>;
		// Публичные отзывы товара со сводкой (fd-review-02).
		reviews(id: string, query?: ProductReviewsQuery): Promise<ProductReviewList>;
		/**
		 * Вопросы к товару с ответами магазина (fd-review-03): вкладка
		 * «Вопросы» панели «Отзывы и вопросы» карточки. Задать вопрос -
		 * `buyer.askQuestion`.
		 */
		questions(id: string): Promise<ProductQuestionList>;
	};
	categories: {
		list(): Promise<Category[]>;
		/** Архивные узлы с преемником (старые ссылки cat-3, cat-8…). */
		archived(): Promise<ArchivedCategory[]>;
	};
	shops: {
		list(): Promise<Shop[]>;
		get(slug: string): Promise<Shop>;
		storefront(slug: string): Promise<Storefront>;
		/** fd-boutique-13: индивидуальный заказ с витрины магазина (вошедший покупатель). */
		createCustomOrder(slug: string, input: CreateShopCustomOrder): Promise<ShopCustomOrder>;
	};
	cart: {
		create(): Promise<Cart>;
		get(id: string): Promise<Cart>;
		addItem(id: string, req: AddCartItemRequest): Promise<Cart>;
		/** Массовое удаление позиций одним запросом. */
		removeItems(id: string, itemIds: string[]): Promise<Cart>;
		updateItem(id: string, itemId: string, qty: number): Promise<Cart>;
		removeItem(id: string, itemId: string): Promise<Cart>;
		applyPromo(id: string, code: string): Promise<Cart>;
		clearPromo(id: string): Promise<Cart>;
		/** Лента промокодов корзины: коды покупателя и магазинов из корзины с применимостью. */
		promoFeed(id: string): Promise<CartPromoFeed>;
	};
	orders: {
		// Create a pending order from the cart (authoritative total + invoiceId);
		// the buyer then pays via the widget and the order is finalized on confirm.
		createPending(req: CreateOrderRequest): Promise<Order>;
		confirm(id: string): Promise<Order>;
		/** fd-payment-01: оплатить заказ сохранённой картой. */
		paySavedCard(id: string, paymentMethodId: string): Promise<SavedCardPayResult>;
		get(id: string): Promise<Order>;
		// Carrier shipments for an order (tracking on the receipt).
		shipments(id: string): Promise<OrderShipment[]>;
	};
	payments: {
		config(): Promise<PublicPaymentConfig>;
		/** Адрес платёжной страницы провайдера для редирект-оплаты (Робокасса). */
		url(orderId: string): Promise<{ url: string }>;
	};
	settings: {
		delivery(): Promise<DeliveryConfig>;
	};
	delivery: {
		// Live delivery options per shop group for a cart + destination.
		quote(req: DeliveryQuoteRequest): Promise<DeliveryQuote>;
		// Pickup points for a destination city (carrier-backed; [] when off).
		// `dressingRoom` фильтрует выдачу до ПВЗ с примерочной (СДЭК примерка).
		points(req: { country?: string; city: string; postalCode?: string; dressingRoom?: boolean }): Promise<PickupPoint[]>;
		// Своя доставка продавцов корзины в город покупателя.
		selfOptions(req: { cartId: string; city: string; country?: string; lineIds?: string[] }): Promise<SelfDeliveryShopOption[]>;
		// Самовывоз от продавцов корзины: точки магазинов.
		sellerPickupOptions(req: { cartId: string; city: string; country?: string; lineIds?: string[] }): Promise<SellerPickupShopOption[]>;
		// Как продавец доставит товар в город покупателя (карточка товара).
		/** `variantId` - выбранный вариант: цена и габариты строки (fd-catalog-08). */
		productOptions(req: {
			productId: string;
			city: string;
			country?: string;
			variantId?: string;
		}): Promise<ProductDeliveryOptions>;
	};
	geo: {
		countries(): Promise<Country[]>;
		currencies(): Promise<Currency[]>;
		// Address autocomplete for checkout; [] when the lookup is unconfigured.
		// `city` scopes the street search to the chosen locality.
		reverse(lat: number, lon: number): Promise<GeoReverse>;
		addressSuggest(query: string, city?: string): Promise<AddressSuggestion[]>;
		// City autocomplete for the checkout city field; [] when unconfigured.
		citySuggest(query: string): Promise<CitySuggestion[]>;
	};
	feed: {
		// Единая управляемая выдача главной. `segment` фильтрует аудиторию
		// (женское/мужское/детское); общие для всех блоки приходят всегда.
		blocks(segment?: FeedSegment, department?: FeedDepartment): Promise<FeedBlockWire[]>;
		collections(): Promise<FeedCollection[]>;
		collection(slug: string): Promise<FeedCollection>;
		/** Файл картинки идеи для «Скачать» (A-24): blob по прямой ссылке на медиа. */
		imageFile(url: string): Promise<Blob>;
		productCollections(productId: string): Promise<ProductCollectionParticipation[]>;
		/** Персональная лента товаров главной: ранжирование по профилю покупателя. */
		forYou(query: PersonalFeedQuery): Promise<PersonalFeedPage>;
	};
	// Телеметрия витрины: из неё считается воронка продавца и просмотры
	// карточки товара. Единственный потребитель — `@/lib/discovery`.
	discovery: {
		recordEvents(input: DiscoveryEventBatch): Promise<void>;
		/**
		 * Шаг «заказ» (fd-discovery-06): после оформления - по событию на товар
		 * из идеи с номером заказа (одиночная ручка: пачка заказ не несёт).
		 */
		recordOrderEvent(input: { clientId?: string; orderId: string; productId: string; collectionId: string; collectionSlug?: string }): Promise<void>;
	};
	// Партнёрская ссылка ?promo=CODE: переход (верх воронки кода) и атрибуция
	// уже вошедшего покупателя (только новый и ещё ничей — решает шлюз).
	referral: {
		visit(code: string, visitor: string, meta?: ReferralVisitMeta): Promise<void>;
		attribute(code: string): Promise<{ attributed: boolean }>;
	};
	// Ссылка магазина /s/<код> (клиенты продавцов, 17.10.2026): магазин по
	// коду, переход и привязка вошедшего покупателя (правила решает шлюз).
	shopInvite: {
		resolve(code: string): Promise<ShopInvite | null>;
		visit(code: string, visitor: string, meta?: ReferralVisitMeta): Promise<void>;
		bind(code: string, visitor?: string): Promise<ShopInviteBindResult>;
	};
	// Удаление аккаунта (App Store 5.1.1(v), Google Play, 152-ФЗ).
	accountDeletion: {
		check(): Promise<AccountDeletionCheck>;
		start(): Promise<{ sessionId: string; maskedTarget: string; expiresAt: string }>;
		confirm(sessionId: string, code: string): Promise<AccountDeletionResult>;
	};
	// Вход через Яндекс ID и VK ID: кнопки входа, обмен билета, привязки в профиле.
	oauth: {
		providers(): Promise<OAuthProviderInfo[]>;
		start(provider: OAuthProviderId, input: { mode?: "login" | "link"; returnTo?: string }): Promise<{ url: string }>;
		exchange(ticket: string): Promise<OAuthExchangeResult>;
		claim(ticket: string): Promise<OAuthIdentity[]>;
		identities(): Promise<OAuthIdentity[]>;
		unlink(provider: OAuthProviderId): Promise<void>;
	};
	buyer: {
		startAuth(input: BuyerAuthStartRequest): Promise<BuyerAuthStartResponse>;
		verifyAuth(input: BuyerAuthVerifyRequest): Promise<BuyerAuthVerifyResponse>;
		logout(): Promise<void>;
		me(): Promise<BuyerMe>;
		updateProfile(input: BuyerProfilePatch): Promise<BuyerMe>;
		// §5 расширенный профиль, аватар, смена телефона (веб-макет «Личные данные»).
		updateProfileExt(input: BuyerProfileExtPatch): Promise<BuyerMe>;
		uploadAvatar(file: File): Promise<{ avatarUrl: string }>;
		deleteAvatar(): Promise<void>;
		startPhoneChange(): Promise<PhoneChangeStart>;
		verifyPhoneChangeCurrent(sessionId: string, code: string): Promise<PhoneChangeVerify>;
		setPhoneChangeNew(sessionId: string, phone: string): Promise<PhoneChangeNew>;
		verifyPhoneChangeNew(sessionId: string, code: string): Promise<BuyerMe>;
		listSessions(): Promise<BuyerSession[]>;
		revokeSession(id: string): Promise<void>;
		revokeOtherSessions(): Promise<void>;
		listAddresses(): Promise<BuyerAddress[]>;
		createAddress(input: BuyerAddressInput): Promise<BuyerAddress>;
		updateAddress(id: string, input: BuyerAddressInput): Promise<BuyerAddress>;
		deleteAddress(id: string): Promise<void>;
		// Оформление заказа: получатели, ранее выбранные ПВЗ, подсказка по ИНН.
		listRecipients(): Promise<BuyerRecipient[]>;
		createRecipient(input: BuyerRecipientInput): Promise<BuyerRecipient>;
		updateRecipient(id: string, input: BuyerRecipientInput): Promise<BuyerRecipient>;
		deleteRecipient(id: string): Promise<void>;
		listPickupPoints(): Promise<SavedPickupPoint[]>;
		savePickupPoint(input: SavedPickupPointInput): Promise<SavedPickupPoint>;
		deletePickupPoint(carrier: string, code: string): Promise<void>;
		suggestCompany(query: string): Promise<BuyerCompanySuggestion[]>;
		listPaymentMethods(): Promise<BuyerPaymentMethod[]>;
		createPaymentMethod(input: BuyerPaymentMethodInput): Promise<BuyerPaymentMethod>;
		// fd-payment-01: переименовать способ и/или сделать его основным.
		updatePaymentMethod(id: string, patch: BuyerPaymentMethodPatch): Promise<BuyerPaymentMethod>;
		deletePaymentMethod(id: string): Promise<void>;
		// §3 раздельное создание: СБП по банку из справочника, расчётный счёт по ИНН.
		listSbpBanks(): Promise<SbpBank[]>;
		createSbpPaymentMethod(input: { bankId: string; label?: string }): Promise<BuyerPaymentMethod>;
		createCompanyPaymentMethod(input: { inn: string; label?: string }): Promise<BuyerPaymentMethod>;
		// fd-payment-03: внутренний баланс, история операций и пополнение.
		getBalance(): Promise<BuyerBalance>;
		listLedger(): Promise<LedgerEntry[]>;
		topupBalance(amount: number): Promise<BalanceTopup>;
		/** Робокасса: ссылка на оплату пополнения (у виджетных провайдеров — 501). */
		topupPaymentUrl(id: string): Promise<{ url: string }>;
		confirmTopup(id: string): Promise<TopupConfirmResult>;
		// fd-payment-02: реквизиты ЮЛ покупателя + счёт по заказу.
		getBillingProfile(): Promise<BillingProfile | null>;
		updateBillingProfile(input: BillingProfileInput): Promise<BillingProfile>;
		getOrderInvoice(id: string): Promise<Invoice>;
		listOrders(): Promise<Order[]>;
		getOrder(id: string): Promise<Order>;
		cancelOrder(id: string, reason?: string): Promise<Order>;
		repeatOrder(id: string): Promise<Cart>;
		reviewOrder(id: string, input: { rating: number; text?: string; productId?: string }): Promise<Order>;
		requestOrderReturn(id: string, input: { kind?: "return" | "dispute"; reason: string }): Promise<Order>;
		completeOrder(id: string): Promise<Order>;
		listMessages(): Promise<BuyerMessage[]>;
		listConversations(): Promise<ChatConversation[]>;
		/**
		 * Открыть (или продолжить) чат с поддержкой площадки. Один тред на
		 * аккаунт: вход в поддержку должен быть один, а не «где-то в разделе
		 * помощи и отдельно в чатах».
		 */
		openSupportConversation(message?: string): Promise<ChatConversation>;
		/**
		 * Завести тред по товару первым сообщением (POST /buyer/conversations).
		 * Шлюз прикрепляет к нему карточку товара и не плодит дубли: второй вызов
		 * по тому же товару пишет в уже заведённый тред.
		 */
		startProductConversation(input: { productId: string; message: string }): Promise<ChatConversation>;
		/**
		 * fd-boutique-01: «Написать продавцу» из шапки витрины — тред с
		 * магазином без товара (POST /buyer/conversations/shop). Один тред на
		 * пару покупатель-магазин: повтор пишет в него же.
		 */
		startShopConversation(input: { shopSlug: string; message: string; imageUris?: string[] }): Promise<ChatConversation>;
		/** fd-boutique-13: заявки на индивидуальный заказ (все или одного магазина). */
		listCustomOrders(shopId?: string): Promise<ShopCustomOrderList>;
		cancelCustomOrder(id: string): Promise<ShopCustomOrder>;
		getConversation(id: string): Promise<ChatConversation>;
		/**
		 * Сообщение в тред. `brief.productId` — товар, прикреплённый в композере
		 * (пришли в заведённый тред с карточки «В чат с продавцом»): шлюз сам
		 * соберёт карточку товара во вложение. Текст может быть пустым, если есть
		 * фото или товар.
		 */
		replyConversation(
			id: string,
			body: string,
			imageUris?: string[],
			brief?: { productId?: string },
		): Promise<ChatMessage>;
		// Веб «Чаты»: прочитано, режим выбора (закрепить / удалить у себя), фото.
		markConversationRead(id: string): Promise<void>;
		pinConversations(ids: string[], pinned: boolean): Promise<{ updated: number }>;
		deleteConversations(ids: string[]): Promise<{ updated: number }>;
		uploadChatImage(file: File): Promise<{ url: string }>;
		acceptOffer(offerId: string, cartId: string): Promise<Cart>;
		// Запросы покупателя на поиск и изготовление (fd-request-*).
		listRequests(): Promise<BuyerRequestList>;
		getRequest(id: string): Promise<BuyerRequestDetail>;
		createRequest(input: CreateBuyerRequest): Promise<BuyerRequestDetail>;
		// Правка разрешена только до первого отклика (fd-request-17).
		updateRequest(id: string, input: UpdateBuyerRequest): Promise<BuyerRequestDetail>;
		cancelRequest(id: string): Promise<BuyerRequestDetail>;
		addRequestAttachment(id: string, file: File): Promise<BuyerRequestAttachment>;
		// Отклонить отклик продавца (fd-request-11a); принять — через acceptOffer.
		declineOffer(offerId: string): Promise<RequestOffer>;
		// Принять отклик без товара (fd-request-10): в чат, где продавец выставит смету.
		acceptOfferInChat(offerId: string): Promise<RequestOffer>;
		listProductLikes(input?: { page?: number; pageSize?: number }): Promise<PaginatedLikeIds>;
		listShopLikes(input?: { page?: number; pageSize?: number }): Promise<PaginatedLikeIds>;
		listIdeaLikes(input?: { page?: number; pageSize?: number }): Promise<PaginatedLikeIds>;
		setProductLiked(productId: string, liked: boolean, size?: string): Promise<LikeTargetResult>;
		setShopLiked(shopId: string, liked: boolean): Promise<LikeTargetResult>;
		setIdeaLiked(ideaId: string, liked: boolean): Promise<LikeTargetResult>;
		/** Сердечко у отзыва на карточке товара: `PUT /buyer/likes/reviews/:id`. */
		setReviewLiked(reviewId: string, liked: boolean): Promise<LikeTargetResult>;
		/** Вопрос к товару (fd-review-03): `POST /buyer/products/:id/questions`. */
		askQuestion(productId: string, body: string): Promise<ProductQuestion>;
		/** Скрытые покупателем идеи (A-26): `GET /buyer/ideas/hidden`. */
		listHiddenIdeas(): Promise<HiddenIdeaState>;
		setIdeaHidden(ideaId: string, hidden: boolean): Promise<HiddenTargetResult>;
		importGuestLikes(clientId: string): Promise<LikeState>;
		/** Гость → аккаунт: слить корзину, избранное и просмотры устройства с
		 *  аккаунтом. Идемпотентно — вызывается при входе и перед оформлением. */
		attachDevice(device: { clientId?: string; cartId?: string | null }): Promise<DeviceAttachResult>;
	};
	notifications: {
		list(query: NotificationListQuery): Promise<NotificationPage>;
		unreadCount(): Promise<NotificationUnreadCount>;
		detail(id: string, locale: NotificationLocale): Promise<NotificationItem>;
		markRead(id: string): Promise<void>;
		markAllRead(): Promise<void>;
		markReadBySource(request: NotificationReadBySourceRequest): Promise<void>;
		// Настройки уведомлений покупателя (fd-notif-02/14/15/17/20).
		getPreferences(): Promise<NotificationPreferences>;
		updatePreferences(patch: NotificationPreferencesUpdate): Promise<NotificationPreferences>;
		// Веб-макет «Профиль»: темы покупателя — настройки, счётчики, скрытие из ленты.
		getTopicSettings(): Promise<NotificationTopicSettings>;
		updateTopicSettings(patch: NotificationTopicSettingsUpdate): Promise<NotificationTopicSettings>;
		// fd-notif-17: счётчики «Показать N» с учётом статуса (все / непрочитанные / прочитанные).
		topicCounts(filter?: "all" | "unread" | "read"): Promise<NotificationTopicCounts>;
		archive(id: string): Promise<void>;
		archiveAll(topics?: NotificationTopic[]): Promise<void>;
		// fd-platform-04: Telegram — статус привязки, ссылка t.me/<бот>?start=<код>, отвязка.
		telegramStatus(): Promise<TelegramLinkStatus>;
		telegramLink(): Promise<TelegramLinkStart>;
		telegramUnlink(): Promise<void>;
	};
	// Центр помощи покупателя (fd-support-01).
	support: {
		faq(category?: string): Promise<FaqList>;
		listTickets(): Promise<SupportTicket[]>;
		getTicket(id: string): Promise<SupportTicketDetail>;
		createTicket(input: CreateTicketRequest): Promise<SupportTicketDetail>;
		addMessage(id: string, body: string): Promise<SupportTicketDetail>;
		closeTicket(id: string): Promise<SupportTicketDetail>;
	};
	// Публичные настройки витрины (fd-analytics-05).
	public: {
		metrika(): Promise<MetrikaConfig>;
	};
	engagement: {
		getLikes(clientId: string): Promise<LikeState>;
		setProductLiked(clientId: string, productId: string, liked: boolean, size?: string): Promise<LikeTargetResult>;
		setShopLiked(clientId: string, shopId: string, liked: boolean): Promise<LikeTargetResult>;
		setIdeaLiked(clientId: string, ideaId: string, liked: boolean): Promise<LikeTargetResult>;
		/** Скрытые гостем идеи (A-26): `GET /ideas/hidden?clientId=`. */
		getHiddenIdeas(clientId: string): Promise<HiddenIdeaState>;
		setIdeaHidden(clientId: string, ideaId: string, hidden: boolean): Promise<HiddenTargetResult>;
		sendProductMessage(input: {
			productId: string;
			clientId?: string;
			contact?: string;
			message: string;
		}): Promise<ProductMessage>;
		createProductManufacturingRequest(input: {
			productId: string;
			clientId?: string;
			contact?: string;
			message?: string;
			selectedOptions?: string[];
		}): Promise<ProductManufacturingRequest>;
	};	// SEO веб-витрины (fd-web-03): мета-данные страницы для usePageMeta.
	seo: {
		page(path: string): Promise<SeoPage>;
	};
	// Web Push веб-витрины (fd-web-04): ключ VAPID и подписка браузера.
	push: {
		config(): Promise<WebPushConfig>;
		subscribe(input: WebPushSubscriptionInput): Promise<void>;
		unsubscribe(endpoint: string): Promise<void>;
	};
};
